/**
 * DrawerSection — the canonical collapsible section primitive used by both
 * ora's WorkflowDetailDrawer and the Outlook add-in's InstanceDetailDrawer.
 *
 * PLAIN DOM, NO MUI — deliberately. This was built on MUI <Accordion> until
 * monitored-mailboxes.tsx became the first route to SERVER-RENDER it. Every
 * other consumer is a drawer body that mounts on interaction, so MUI's styles
 * were always injected client-side by emotion and nobody noticed that this app
 * has no emotion SSR setup (no createCache / CacheProvider / style extraction
 * in entry.server.tsx). Server-rendered, the same component produced a
 * hydration mismatch (React #418/#425 → #423 bail-out) AND lost MUI's base
 * CSS: without `MuiSvgIcon-root` the chevron has no intrinsic size and fills
 * its container, and without MUI supplying `display:flex` the summary fell back
 * to `display:block`, dropping the chevron below the title.
 *
 * Both failures are structural, so the fix is structural: the chevron carries
 * explicit width/height ATTRIBUTES (it cannot balloon with or without CSS), and
 * the summary owns its own `display:flex`. Nothing here depends on a runtime
 * style injector, so it renders identically on the server and the client.
 *
 * The chevron path is MUI's own ChevronRight glyph, so the 25 existing
 * consumers look exactly as they did.
 *
 * Styling lives in the Tailwind component tokens drawer-accordion-frame,
 * drawer-accordion-summary, drawer-accordion-chevron, drawer-accordion-body and
 * drawer-accordion-title. They are duplicated in the add-in's
 * styles.css because the add-in ships its own bundle — mirror any change
 * there.
 *
 * State behaviour (PR-2):
 *   - `defaultExpanded` controls initial open state when no persisted value
 *     exists. Per-app callers pass this based on workflow status family
 *     (e.g. Client always expanded, Run expanded in terminal, etc.).
 *   - `persistKey` (when set) reads/writes the user's toggle to
 *     localStorage so the choice carries across navigation within the same
 *     family. Recommended key: `drawer:${family}:${section}`.
 *   - **Lazy mount:** the body renders `null` until the section has been
 *     expanded at least once. Once mounted, content stays mounted across
 *     re-collapse so re-expanding is instant. Defers fetchers in
 *     ArtifactsSection / HitlHistorySection / WorkflowResultSection until
 *     the user actually opens those sections.
 *   - Respects `prefers-reduced-motion` — now in CSS rather than through a
 *     JS media-query hook, which removes the last reason this file had to
 *     reach for a component library and is one less thing to get wrong on the
 *     server, where no media query can be evaluated at all.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from "react";

// Exposes the enclosing DrawerSection's current expanded state to descendants
// so fetch-bearing children (ArtifactsSection, HitlHistorySection, …) can
// defer their work while the user pages through instances with the section
// collapsed. ``hasEverBeenOpen`` mounts the DOM lazily, but children stay
// mounted across collapse — without this signal, a useEffect keyed on
// instanceId fires for every drawer-paging step even though nothing is
// visible. Default value is ``expanded: true`` so consumers outside a
// DrawerSection (tests, other surfaces) keep their previous behavior.
const DrawerSectionContext = createContext<{ expanded: boolean }>({
  expanded: true,
});

export function useDrawerSectionExpanded(): boolean {
  return useContext(DrawerSectionContext).expanded;
}

export interface DrawerSectionProps {
  title: string;
  defaultExpanded?: boolean;
  /** localStorage key for user-overridden expansion. e.g. `drawer:${family}:${section}`. */
  persistKey?: string;
  rightAdornment?: ReactNode;
  className?: string;
  children: ReactNode;
  /** Invoked when the section's expanded state changes — including the
   *  initial state read from ``defaultExpanded`` / localStorage on mount.
   *  Lets parents that fetch the section's data (e.g. the plugin's
   *  InstanceDetailDrawer, which fetches artifacts at the outer level
   *  rather than inside the section body) defer the network call until
   *  the user actually opens the section. For fetch logic that lives
   *  INSIDE the section body, prefer ``useDrawerSectionExpanded()`` —
   *  it's a one-liner with no plumbing. */
  onExpandedChange?: (expanded: boolean) => void;
}

function readPersistedExpanded(
  persistKey: string | undefined,
  fallback: boolean,
): boolean {
  if (!persistKey || typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(persistKey);
    if (raw === "true") return true;
    if (raw === "false") return false;
  } catch {
    // localStorage unavailable (private mode, quota, etc.) — fall through.
  }
  return fallback;
}

export function DrawerSection({
  title,
  defaultExpanded = true,
  persistKey,
  rightAdornment,
  className,
  children,
  onExpandedChange,
}: DrawerSectionProps) {
  // The FIRST render — server and client's pre-hydration pass alike — must
  // use `defaultExpanded` only. localStorage doesn't exist during SSR, so
  // reading it synchronously in the initializer made the client's first
  // render disagree with the server-rendered markup whenever the persisted
  // value differed from defaultExpanded. The persisted value is applied from
  // the effect below instead, which only ever runs client-side, after
  // hydration has already reconciled against the server markup. We
  // deliberately do NOT re-read localStorage on prop changes — persistence is
  // a startup-time concern; family-driven default changes should reset their
  // own persisted state by using a different persistKey.
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [hasEverBeenOpen, setHasEverBeenOpen] = useState(defaultExpanded);

  // Apply the persisted choice (if any), client-side only, then notify the
  // parent of the resulting mount-time state so a parent that fetches on
  // accordion-open knows whether to fire immediately. Latest-callback-via-ref
  // so the effect runs exactly once without invalidating on every render.
  // Subsequent changes are reported from handleToggle below.
  const onExpandedChangeRef = useRef(onExpandedChange);
  onExpandedChangeRef.current = onExpandedChange;
  useEffect(() => {
    const persisted = readPersistedExpanded(persistKey, defaultExpanded);
    if (persisted !== defaultExpanded) {
      setExpanded(persisted);
      if (persisted) setHasEverBeenOpen(true);
    }
    onExpandedChangeRef.current?.(persisted);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional fire-once on mount
  }, []);

  // useId, not a counter or a random string: it is stable across the server
  // render and hydration, which is the whole point of this rewrite.
  const reactId = useId();
  const headerId = `drawer-section-header-${reactId}`;
  const bodyId = `drawer-section-body-${reactId}`;

  const handleToggle = useCallback(() => {
    const next = !expanded;
    setExpanded(next);
    if (next) setHasEverBeenOpen(true);
    onExpandedChange?.(next);
    if (persistKey && typeof window !== "undefined") {
      try {
        window.localStorage.setItem(persistKey, String(next));
      } catch {
        // localStorage unavailable — silently drop the write.
      }
    }
  }, [expanded, persistKey, onExpandedChange]);

  const frameClass = ["drawer-accordion-frame", className]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={frameClass}>
      <button
        type="button"
        id={headerId}
        className="drawer-accordion-summary"
        aria-expanded={expanded}
        aria-controls={bodyId}
        onClick={handleToggle}
      >
        {/* MUI's own ChevronRight glyph, with width/height as ATTRIBUTES so it
            has an intrinsic size even before (or without) any stylesheet. The
            0°→90° rotation is driven off aria-expanded in CSS, so the DOM
            carries no duplicate state. */}
        <svg
          className="drawer-accordion-chevron"
          aria-hidden="true"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
        </svg>
        <div className="drawer-accordion-title flex w-full items-center">
          <span className="flex-1">{title}</span>
          {rightAdornment ? (
            <span className="ml-2 flex items-center min-w-0">{rightAdornment}</span>
          ) : null}
        </div>
      </button>
      {/* Horizontal padding matches the summary's own 0.75rem inset (see
          .drawer-accordion-summary in tailwind.css) so the body's left/right
          edges line up with the title text instead of sitting flush with the
          unframed area below the title bar. */}
      <div
        id={bodyId}
        role="region"
        aria-labelledby={headerId}
        className="drawer-accordion-body"
        data-expanded={expanded}
      >
        {/* The clip element is what the grid-rows transition collapses; it must
            own the overflow so the body's own padding does not leak out of a
            zero-height row mid-animation. */}
        <div className="drawer-accordion-body-clip">
          <div className="px-3 pb-2 pt-3">
            {hasEverBeenOpen ? (
              <DrawerSectionContext.Provider value={{ expanded }}>
                <div className="flex w-full flex-col gap-1.5">{children}</div>
              </DrawerSectionContext.Provider>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export default DrawerSection;
